import { io, Socket } from 'socket.io-client';

// Store socket and service in window to survive Hot Module Reload in development
declare global {
  interface Window {
    __socketIO?: Socket;
    __socketIOService?: SocketIOService;
  }
}

class SocketIOService {
  private socket: Socket | null = null;
  private newAppointmentListeners: ((data: any) => void)[] = [];
  private calendarUpdateListeners: ((data: any) => void)[] = [];
  private confirmedAppointmentListeners: ((data: any) => void)[] = [];
  private deletedAppointmentListeners: ((data: any) => void)[] = [];
  private appointmentMovedListeners: ((data: any) => void)[] = [];
  private customEventListeners: { [eventName: string]: ((data: any) => void)[] } = {};
  private isConnected: boolean = false;
  private pendingJoinRoom: string | number | null = null;

  constructor() {
  }

  private connect() {
    // Check if socket exists in window (survives HMR)
    if (window.__socketIO && window.__socketIO.connected) {
      this.socket = window.__socketIO;
      this.isConnected = true;
      return;
    }

    if (this.socket && this.socket.connected) {
      return; // Prevent double connection
    }
    if (this.socket && !this.socket.connected) {
      this.socket.connect();
      return;
    }
    const url = process.env.REACT_APP_SOCKET_URL || window.location.origin;

    // Get JWT token from localStorage
    const token = localStorage.getItem('token');

    this.socket = window.__socketIO = io(url, { 
      path: '/socket.io',
      auth: {
        token: token
      },
      query: {
        token: token
      }
    });

    this.socket.on('connect', () => {
      this.isConnected = true;
      if (this.pendingJoinRoom) {
        this.joinRoom(this.pendingJoinRoom);
      } else {
      }

      Object.keys(this.customEventListeners).forEach(eventName => {
        const hasListeners = this.socket?.hasListeners(eventName);

        if (this.socket && !hasListeners) {
          this.socket.on(eventName, (data: any) => {
            this.customEventListeners[eventName]?.forEach(callback => callback(data));
          });
        }
      });
    });
    
    this.socket.on('disconnect', (reason) => {
      this.isConnected = false;
    });
    
    this.socket.on('connect_error', (error) => {
    });

    this.socket.on('new_appointment', (data: any) => {
      this.newAppointmentListeners.forEach((cb, index) => {
        cb(data);
      });
    });
    const listenersAfter = this.socket.listeners('new_appointment').length;
    this.socket.on('confirmed_appointment', (data: any) => {
      this.confirmedAppointmentListeners.forEach(cb => cb(data));
    });
    this.socket.on('deleted_appointment', (data: any) => {
      this.deletedAppointmentListeners.forEach(cb => cb(data));
    });
    this.socket.on('calendar_update', (data: any) => {
      this.calendarUpdateListeners.forEach(cb => cb(data));
    });
    
    this.socket.on('appointment_moved', (data: any) => {
      this.appointmentMovedListeners.forEach(cb => cb(data));
    });
  }

  public joinRoom(userId: string | number) {
    if (!this.socket) {
      this.connect();
    }
    if (this.socket && this.isConnected) {
      this.socket.emit('join', String(userId));
      this.pendingJoinRoom = null;
    } else {
      this.pendingJoinRoom = userId;
    }
  }

  public subscribeNewAppointment(cb: (data: any) => void) {
    this.newAppointmentListeners.push(cb);
    return () => {
      this.newAppointmentListeners = this.newAppointmentListeners.filter(fn => fn !== cb);
    };
  }

  public subscribeConfirmedAppointment(cb: (data: any) => void) {
    this.confirmedAppointmentListeners.push(cb);
    return () => {
      this.confirmedAppointmentListeners = this.confirmedAppointmentListeners.filter(fn => fn !== cb);
    };
  }

  public subscribeDeletedAppointment(cb: (data: any) => void) {
    this.deletedAppointmentListeners.push(cb);
    return () => {
      this.deletedAppointmentListeners = this.deletedAppointmentListeners.filter(fn => fn !== cb);
    };
  }

  public subscribeCalendarUpdate(cb: (data: any) => void) {
    this.calendarUpdateListeners.push(cb);
    return () => {
      this.calendarUpdateListeners = this.calendarUpdateListeners.filter(fn => fn !== cb);
    };
  }

  public subscribeAppointmentMoved(cb: (data: any) => void) {
    this.appointmentMovedListeners.push(cb);
    return () => {
      this.appointmentMovedListeners = this.appointmentMovedListeners.filter(fn => fn !== cb);
    };
  }

  public subscribeToEvent(eventName: string, cb: (data: any) => void) {
    if (!this.customEventListeners[eventName]) {
      this.customEventListeners[eventName] = [];
    }

    this.customEventListeners[eventName].push(cb);

    // Ensure socket is connected and set up listener if not already done
    if (!this.socket) {
      this.connect();
    }

    // Set up socket listener for this event if socket exists and listener not already set
    const hasListeners = this.socket?.hasListeners(eventName);

    if (this.socket && !hasListeners) {
      this.socket.on(eventName, (data: any) => {
        this.customEventListeners[eventName]?.forEach(callback => callback(data));
      });
    } else if (this.socket && hasListeners) {
    }

    return () => {
      if (this.customEventListeners[eventName]) {
        this.customEventListeners[eventName] = this.customEventListeners[eventName].filter(fn => fn !== cb);
      }
    };
  }

  public disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }
}

// Reuse existing service instance from window to survive HMR, or create new one
if (!window.__socketIOService) {
  window.__socketIOService = new SocketIOService();
}

export const socketIOService = window.__socketIOService; 